// Route: /flashcards — библиотека наборов карточек.
// URL state: ?view=grid|table, ?sort=updated|due|accuracy, ?q=, ?demo=
//
// См. docs/ui-spec/flashcards.library.{desktop,mobile}.md.

import { tolerantSearch } from "@studyhub/shared/utils/tolerant-search";
import { Button } from "@studyhub/ui/components/button";
import { EmptyState } from "@studyhub/ui/components/empty-state";
import { ErrorState } from "@studyhub/ui/components/error-state";
import { SegmentedControl } from "@studyhub/ui/components/segmented-control";
import { Skeleton } from "@studyhub/ui/components/skeleton";
import { SectionHeader } from "@studyhub/ui/components/studyhub/section-header";
import {
	IconArrowRight,
	IconBookmark,
	IconBookmarkFilled,
	IconCards,
	IconPlus,
	IconSparkles,
} from "@tabler/icons-react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
	type FlashcardDto,
	useFlashcardSets,
	useMarkedFlashcards,
} from "@/components/flashcards/_api";
import { CreateSetDialog } from "@/components/flashcards/library/create-set-dialog";
import {
	FlashcardsSortSelect,
	type LibraryFilters,
} from "@/components/flashcards/library/filters-bar";
import { GenerateDialog } from "@/components/flashcards/library/generate-dialog";
import { SetCard } from "@/components/flashcards/library/set-card";
import { useMaterialsList } from "@/components/materials/_api";
import {
	CreateEmptyTile,
	LIST_PAGE_GRID,
	LIST_PAGE_SHELL,
	LIST_PAGE_STACK,
	ListToolbar,
} from "@/components/shared/entity-list";
import { PagePendingFallback } from "@/components/shared/page-pending-fallback";
import {
	SectionIntro,
	SectionIntroReopen,
	useSectionIntro,
} from "@/components/shared/section-intro";
import { useArtifactWatching } from "@/hooks/use-artifact-watching";
import { useFlashcardsListProgress } from "@/hooks/use-list-generation-overlay";
import { markArtifactSeenKey } from "@/hooks/use-mark-artifact-seen";
import { useViewMode } from "@/hooks/use-view-mode";

// Раундальные углы наклона карточек — «живая» картотека (TiltCard выпрямляет на hover).
const TILTS = [-1.6, 1.1, -0.8, 1.4, -1.2, 0.7] as const;

const searchSchema = z.object({
	tab: z.enum(["sets", "bookmarks"]).optional(),
	sort: z.enum(["updated", "due", "accuracy"]).optional(),
	q: z.string().optional(),
	materialId: z.string().optional(),
	demo: z.enum(["default", "loading", "empty", "error", "streaming", "quota-exceeded"]).optional(),
});

export const Route = createFileRoute("/flashcards/")({
	validateSearch: tolerantSearch(searchSchema),
	component: FlashcardsLibraryPage,
	pendingComponent: PagePendingFallback,
});

function FlashcardsLibraryPage(): React.ReactElement {
	const { t } = useTranslation("flashcards");
	const search = Route.useSearch();
	const navigate = useNavigate({ from: Route.fullPath });
	const intro = useSectionIntro("flashcards");

	const [createOpen, setCreateOpen] = useState(false);
	const [generateOpen, setGenerateOpen] = useState(false);
	const [view, setView] = useViewMode("flashcards");
	const genProgressById = useFlashcardsListProgress();

	const filters: LibraryFilters = useMemo(
		() => ({
			view,
			sort: search.sort ?? "updated",
			q: search.q ?? "",
			materialId: search.materialId,
		}),
		[search, view],
	);

	function updateFilters(next: LibraryFilters) {
		void navigate({
			search: (prev) => ({
				...prev,
				sort: next.sort,
				q: next.q || undefined,
				materialId: next.materialId,
			}),
			replace: true,
		});
	}

	// Контент-режим: список наборов vs cross-set закладки. Хранится в URL,
	// чтобы кнопка «назад» возвращала к выбранной вкладке.
	const tab: "sets" | "bookmarks" = search.tab ?? "sets";
	function setTab(next: "sets" | "bookmarks") {
		void navigate({
			search: (prev) => ({ ...prev, tab: next === "sets" ? undefined : next }),
			replace: true,
		});
	}

	const setsQuery = useFlashcardSets();
	const markedQuery = useMarkedFlashcards();
	const materialsQuery = useMaterialsList();

	const allSets = useMemo(() => setsQuery.data?.items ?? [], [setsQuery.data]);
	const markedCards = useMemo(() => markedQuery.data?.items ?? [], [markedQuery.data]);

	// Presence-aware: наборы в generating на библиотеке → mute notify.
	const pendingFlashcardWatchKeys = useMemo(
		() =>
			allSets
				.filter((s) => s.processingStatus === "generating")
				.map((s) => markArtifactSeenKey("flashcards", s.id)),
		[allSets],
	);
	useArtifactWatching(pendingFlashcardWatchKeys);

	// id → title материала — резолвим имя источника набора на клиенте (как в /notes).
	const materialMap = useMemo(
		() => new Map((materialsQuery.data?.items ?? []).map((m) => [m.id, m.title])),
		[materialsQuery.data],
	);

	const sets = useMemo(() => {
		let items = allSets;
		if (search.demo === "empty") items = [];
		if (filters.materialId) {
			items = items.filter((s) => s.sourceMaterialId === filters.materialId);
		}
		if (filters.q) {
			const q = filters.q.toLowerCase();
			items = items.filter((s) => s.name.toLowerCase().includes(q));
		}
		if (filters.sort === "due") {
			items = items.toSorted((a, b) => b.dueCount - a.dueCount);
		} else if (filters.sort === "accuracy") {
			items = items.toSorted((a, b) => b.accuracy - a.accuracy);
		} else {
			items = items.toSorted((a, b) => b.updatedAt.localeCompare(a.updatedAt));
		}
		return items;
	}, [allSets, filters, search.demo]);

	// Поиск/сорт тулбара работают и на вкладке «Закладки».
	const filteredMarked = useMemo(() => {
		let items = markedCards;
		if (filters.q) {
			const q = filters.q.toLowerCase();
			items = items.filter(
				(c) =>
					c.front.toLowerCase().includes(q) ||
					c.back.toLowerCase().includes(q) ||
					(c.setName?.toLowerCase().includes(q) ?? false),
			);
		}
		if (filters.sort === "due") {
			items = items.toSorted((a, b) => a.srsDueAt.localeCompare(b.srsDueAt));
		} else if (filters.sort === "accuracy") {
			// Per-card accuracy нет — стабильный порядок по front.
			items = items.toSorted((a, b) =>
				a.front.localeCompare(b.front, undefined, { sensitivity: "base" }),
			);
		}
		return items;
	}, [markedCards, filters.q, filters.sort]);

	const isLoading = search.demo === "loading" || setsQuery.isLoading;
	const isError = search.demo === "error" || setsQuery.isError;
	const isStreamingDemo = search.demo === "streaming";

	// Счётчики для подзаголовка.
	const totalCards = allSets.reduce((acc, s) => acc + s.cardCount, 0);
	const totalDue = allSets.reduce((acc, s) => acc + s.dueCount, 0);

	return (
		<div className={LIST_PAGE_SHELL}>
			{/* Header */}
			<SectionHeader
				icon={<IconCards />}
				category="cards"
				title={t("title")}
				description={t("subtitle", {
					sets: allSets.length,
					total: totalCards,
					due: totalDue,
				})}
				actions={
					<div className="flex items-center gap-2">
						<SectionIntroReopen intro={intro} />
						<Button
							type="button"
							variant="outline"
							onClick={() => setCreateOpen(true)}
							className="cursor-pointer"
							aria-label={t("library.setCta")}
						>
							<IconPlus size={16} />
							<span className="hidden sm:inline">{t("library.setCta")}</span>
						</Button>
						<Button
							type="button"
							onClick={() => setGenerateOpen(true)}
							className="cursor-pointer"
							aria-label={t("generate.cta")}
						>
							<IconSparkles size={16} />
							<span className="hidden sm:inline">{t("generate.cta")}</span>
						</Button>
					</div>
				}
			/>

			{/* Баннер-объяснение раздела: текст из манифеста модуля, состояние
			    «свёрнут» — серверное (см. section-intro.tsx). */}
			<SectionIntro intro={intro} />

			{/* Тулбар общий для наборов и закладок: поиск → Наборы/Закладки → сорт → вид */}
			<ListToolbar
				search={{
					value: filters.q,
					onChange: (q) => updateFilters({ ...filters, q }),
					placeholder:
						tab === "bookmarks"
							? t("library.filters.searchBookmarks")
							: t("library.filters.search"),
				}}
				segments={
					<SegmentedControl
						value={tab}
						onValueChange={setTab}
						scrollable
						className="shrink-0"
						aria-label={t("library.tabs.aria")}
						options={[
							{ value: "sets", label: t("library.tabs.sets"), icon: <IconCards size={15} /> },
							{
								value: "bookmarks",
								label: t("library.tabs.bookmarks"),
								icon: <IconBookmark size={15} />,
								count: markedCards.length,
							},
						]}
					/>
				}
				filters={
					<FlashcardsSortSelect
						value={filters.sort}
						onChange={(sort) => updateFilters({ ...filters, sort })}
					/>
				}
				filtersSheet={
					<FlashcardsSortSelect
						stacked
						value={filters.sort}
						onChange={(sort) => updateFilters({ ...filters, sort })}
					/>
				}
				filtersActive={filters.sort !== "updated"}
				view={view}
				onViewChange={setView}
			/>

			{tab === "bookmarks" ? (
				<BookmarksPanel
					cards={filteredMarked}
					loading={markedQuery.isLoading}
					hasQuery={Boolean(filters.q)}
					view={view}
				/>
			) : isLoading ? (
				<div className={LIST_PAGE_GRID}>
					{Array.from({ length: 6 }).map((_, i) => (
						<Skeleton key={i} className="h-48 rounded-[var(--radius-card)]" />
					))}
				</div>
			) : isError ? (
				<ErrorState
					title={t("library.errorTitle")}
					description={t("library.errorSubtitle")}
					action={
						<Button
							variant="outline"
							onClick={() => void setsQuery.refetch()}
							className="cursor-pointer"
						>
							{t("library.retry")}
						</Button>
					}
				/>
			) : sets.length === 0 ? (
				<EmptyState
					icon={<IconCards size={48} stroke={1.5} className="text-muted-foreground" />}
					title={filters.q ? t("library.searchEmpty.title") : t("library.empty.title")}
					description={
						filters.q
							? t("library.searchEmpty.subtitle", { query: filters.q })
							: t("library.empty.subtitle")
					}
					action={
						filters.q ? undefined : (
							<div className="flex flex-wrap justify-center gap-2">
								<Button onClick={() => setGenerateOpen(true)}>
									<IconSparkles size={14} />
									{t("library.empty.generate")}
								</Button>
								<Button variant="ghost" onClick={() => setCreateOpen(true)}>
									{t("library.empty.create")}
								</Button>
							</div>
						)
					}
				/>
			) : (
				<div className={view === "grid" ? LIST_PAGE_GRID : LIST_PAGE_STACK}>
					{sets.map((set, i) => (
						<SetCard
							key={set.id}
							view={view}
							set={
								isStreamingDemo && set.id === sets[0]?.id
									? { ...set, processingStatus: "generating" }
									: set
							}
							tilt={view === "grid" ? TILTS[i % TILTS.length] : undefined}
							progress={
								isStreamingDemo
									? 60
									: (genProgressById[set.id] ??
										(set.processingStatus === "generating" ? 8 : undefined))
							}
							stage={isStreamingDemo ? t("library.stages.generating") : undefined}
							materialTitle={
								set.materialName ??
								(set.sourceMaterialId ? materialMap.get(set.sourceMaterialId) : undefined)
							}
						/>
					))}
					{/* Тактильная плитка «Новый набор» — только в grid. */}
					{view === "grid" && (
						<CreateEmptyTile
							category="cards"
							icon={<IconCards />}
							label={t("library.createSet")}
							onClick={() => setCreateOpen(true)}
						/>
					)}
				</div>
			)}

			<CreateSetDialog open={createOpen} onOpenChange={setCreateOpen} />
			<GenerateDialog
				open={generateOpen}
				onOpenChange={setGenerateOpen}
				forcedState={search.demo === "quota-exceeded" ? "quota-exceeded" : undefined}
			/>
		</div>
	);
}

// ─── Вкладка «Закладки» ────────────────────────────────────────────────
//
// Cross-set карточки, отмеченные закладкой во время сессии (клавиша M / иконка
// закладки). Клик по карточке ведёт в её набор; CTA запускает SRS-сессию по
// виртуальному набору `marked`.

function BookmarksPanel({
	cards,
	loading,
	hasQuery,
	view,
}: {
	cards: FlashcardDto[];
	loading: boolean;
	hasQuery: boolean;
	view: "grid" | "list";
}): React.ReactElement {
	const { t } = useTranslation("flashcards");
	const dueCount = useMemo(
		() => cards.filter((c) => new Date(c.srsDueAt).getTime() <= Date.now()).length,
		[cards],
	);

	if (loading) {
		return (
			<div className={LIST_PAGE_GRID}>
				{Array.from({ length: 6 }).map((_, i) => (
					<Skeleton key={i} className="h-28 rounded-[var(--radius-card)]" />
				))}
			</div>
		);
	}

	if (cards.length === 0) {
		return (
			<EmptyState
				icon={<IconBookmark size={48} stroke={1.5} className="text-muted-foreground" />}
				title={
					hasQuery ? t("library.bookmarks.searchEmptyTitle") : t("library.bookmarks.emptyTitle")
				}
				description={
					hasQuery ? t("library.bookmarks.searchEmptyHint") : t("library.bookmarks.emptyHint")
				}
			/>
		);
	}

	return (
		<div className="space-y-4">
			<div className="flex flex-wrap items-center justify-between gap-2">
				<p className="text-sm text-muted-foreground">
					{t("library.bookmarks.count", { total: cards.length })}
				</p>
				<div className="flex items-center gap-2">
					<Button asChild variant="ghost" size="sm">
						<Link to="/flashcards/marked">
							{t("library.bookmarks.manage")}
							<IconArrowRight size={14} />
						</Link>
					</Button>
					<Button asChild size="sm">
						<Link to="/flashcards/study/$setId" params={{ setId: "marked" }}>
							<IconCards size={15} />
							{t("library.bookmarks.studyAll", { n: dueCount })}
						</Link>
					</Button>
				</div>
			</div>

			<div className={view === "list" ? LIST_PAGE_STACK : LIST_PAGE_GRID}>
				{cards.map((card) => (
					<Link
						key={card.id}
						to="/flashcards/sets/$setId"
						params={{ setId: card.setId }}
						className="group/bookmark relative flex min-w-0 flex-col gap-2 overflow-hidden rounded-card border border-border bg-card p-3.5 text-sm shadow-e1 transition-[transform,box-shadow,border-color] duration-fast ease-spring hover:-translate-y-px hover:border-primary/40 hover:shadow-e2"
					>
						<IconBookmarkFilled
							size={14}
							className="absolute right-3 top-3 shrink-0 text-warning"
						/>
						<p className="line-clamp-3 break-words pr-5 font-medium text-foreground">
							{card.front}
						</p>
						{card.setName ? (
							<span className="inline-flex w-fit max-w-full items-center gap-1 truncate rounded-md bg-[var(--surface-2)] px-2 py-0.5 text-[11px] text-muted-foreground">
								<IconCards size={12} className="shrink-0" />
								<span className="truncate">{card.setName}</span>
							</span>
						) : null}
					</Link>
				))}
			</div>
		</div>
	);
}
